/**
 * @file usePickingTask.ts
 * @description Состояние и мутации одного задания на сборку (phase19.14, W13).
 *
 * Зачем хук существует: у экрана задания десять операций (взять, скан, ±,
 * ручное количество, недостача строки, проведение, исход «недостача»,
 * вернуть в очередь, пересобрать, отменить), и каждая возвращает обновлённое
 * задание целиком. Держать это в странице означало бы 400 строк и десять копий
 * одинакового «поймать ApiError → toast». Источник правды — ответ сервера:
 * локально задание не правим (docs/processes/engineering-principles.md,
 * «один источник правды»).
 *
 * 409 `picking_task_claimed` означает, что задание изменилось у другого
 * сотрудника: показываем toast и сразу перечитываем задание, иначе кладовщик
 * продолжает работать с устаревшим экраном (так же ведёт себя мобильный).
 *
 * phase19.22 (W17-9c, W14-9): этикетка с другой партией не отмечает строку —
 * экран спрашивает «заменить? причина» (`lotMismatch`); при включённом
 * «скан ячейки при сборке» отметка ждёт скана адреса (`binRequest`) и
 * повторяется сама, как только отсканирован нужный `LOC:`. Последний
 * отсканированный адрес уходит в каждую отметку как `confirmedBinCode`.
 */

import { useCallback, useEffect, useState } from "react";

import { usePendingMark, type PickingLotMismatch } from "./usePendingMark";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  isActivePickingTask,
  pickingApi,
  type PickingScanOptions,
  type PickingShortageReason,
  type PickingTaskDetailDto,
} from "../../../lib/api-picking";
import { parsePickingInput } from "../../../lib/picking-search";
import { playScanError, playScanOk } from "../../../lib/scan-feedback";
import { wmsErrorText } from "../../../lib/wms-error-text";

export type { PickingLotMismatch } from "./usePendingMark";

export interface UsePickingTaskResult {
  task: PickingTaskDetailDto | null;
  loading: boolean;
  /** Ошибка загрузки задания (не операции) — показывается вместо экрана. */
  loadError: string | null;
  busy: boolean;
  /** Ошибка последней операции — для inline-Alert в открытом диалоге. */
  actionError: string | null;
  clearActionError: () => void;
  /** Строка, отмеченная последним сканом, — подсветка и автоскролл. */
  scannedItemId: string | null;
  reload: () => Promise<void>;
  claim: () => Promise<void>;
  /** Снять исполнителя / вернуть закрытое задание в очередь (статус → NEW). */
  release: () => Promise<void>;
  /** Отменить задание (админ склада): статус → CANCELLED. */
  cancel: (note?: string) => Promise<boolean>;
  /** Пересобрать строки задания по текущим резервам заказа (NEW/SHORTAGE). */
  rebuild: () => Promise<void>;
  scan: (code: string, options?: PickingScanOptions) => Promise<void>;
  /** Отметка ждёт скана ячейки: адрес строки; `null` — не ждёт. */
  binRequest: string | null;
  lotMismatch: PickingLotMismatch | null;
  confirmLotMismatch: (reason: string) => Promise<void>;
  cancelLotMismatch: () => void;
  setQty: (itemId: string, qty: number) => Promise<void>;
  markShortage: (
    itemId: string,
    input: { shortageQty: number; reason: PickingShortageReason; note?: string },
  ) => Promise<boolean>;
  /** Снять отметку недостачи со строки: товар нашёлся на соседней полке. */
  clearShortage: (itemId: string) => Promise<boolean>;
  complete: () => Promise<boolean>;
  reportShortage: (note: string) => Promise<boolean>;
}

export function usePickingTask(taskId: string | undefined): UsePickingTaskResult {
  const notify = useNotify();
  const [task, setTask] = useState<PickingTaskDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [scannedItemId, setScannedItemId] = useState<string | null>(null);
  // Замена партии и «отсканируйте ячейку» — отложенная отметка (phase19.22).
  const marks = usePendingMark(taskId, {
    onMarked: (next, itemId) => {
      playScanOk();
      setTask(next);
      setScannedItemId(itemId);
    },
    onTask: (next) => {
      // Отметки нет: ждём «заменить партию?» с причиной (W17-9c).
      playScanError();
      setTask(next);
    },
    onBin: (bin) => {
      playScanOk();
      setScannedItemId(null);
      notify.info(`Ячейка ${bin.code}${bin.label ? ` — ${bin.label}` : ""}`);
    },
  });

  const reload = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      setTask(await pickingApi.findOne(taskId));
      setLoadError(null);
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? err.message : "Не удалось загрузить задание",
      );
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Общая обёртка операции: гасит двойные нажатия, кладёт человеческий текст
   * ошибки и в toast, и в `actionError` (диалог показывает его inline).
   */
  const run = useCallback(
    async (
      fn: () => Promise<PickingTaskDetailDto>,
      options: { silent?: boolean } = {},
    ): Promise<boolean> => {
      if (!taskId) return false;
      setBusy(true);
      setActionError(null);
      try {
        setTask(await fn());
        return true;
      } catch (err) {
        const message = wmsErrorText(err, "Не удалось выполнить операцию");
        setActionError(message);
        const stale = isStaleConflict(err);
        // Конфликт показываем всегда — даже в «тихих» операциях диалога:
        // иначе кладовщик не поймёт, почему экран перезагрузился.
        if (!options.silent || stale) notify.error(message);
        if (stale) void reload();
        return false;
      } finally {
        setBusy(false);
      }
    },
    [taskId, notify, reload],
  );

  const claim = useCallback(async () => {
    if (!taskId) return;
    const ok = await run(() => pickingApi.claim(taskId));
    if (ok) notify.success("Задание взято в работу");
  }, [taskId, run, notify]);

  const release = useCallback(async () => {
    if (!taskId) return;
    // Из недостачи/отмены задание возвращается в очередь — формулировка другая.
    const returning = task ? !isActivePickingTask(task.status) : false;
    const ok = await run(() => pickingApi.release(taskId));
    if (ok) {
      notify.success(
        returning ? "Задание возвращено в очередь" : "Задание освобождено",
      );
    }
  }, [taskId, run, notify, task]);

  const cancel = useCallback(
    async (note?: string) => {
      if (!taskId) return false;
      const ok = await run(() => pickingApi.cancel(taskId, note), { silent: true });
      if (ok) notify.warning("Задание отменено — заказ остался на своей стадии");
      return ok;
    },
    [taskId, run, notify],
  );

  const rebuild = useCallback(async () => {
    if (!taskId) return;
    const ok = await run(() => pickingApi.rebuild(taskId));
    if (ok) notify.success("Задание пересобрано по текущим резервам заказа");
  }, [taskId, run, notify]);

  /**
   * Скан: новый код товара снимает старую отложенную отметку; ответ разбирает
   * `usePendingMark.execute` (ячейка, замена партии, повтор отметки).
   */
  async function scan(code: string, options: PickingScanOptions = {}) {
    if (!taskId) return;
    const goods = parsePickingInput(code).kind !== "bin";
    if (goods) marks.clearPending();
    setBusy(true);
    setActionError(null);
    try {
      await marks.execute(code, options);
    } catch (err) {
      playScanError();
      // Скан самой ячейки ждать нечего — ждём только отметки товара, со всеми
      // её параметрами (замена партии, причина).
      if (goods) {
        marks.deferIfBinRequired(err, {
          type: "scan",
          code,
          options: { confirmLotMismatch: options.confirmLotMismatch, reason: options.reason },
        });
      }
      const message = wmsErrorText(err, "Код не распознан. Введите артикул вручную");
      setActionError(message);
      notify.error(message);
      if (isStaleConflict(err)) void reload();
    } finally {
      setBusy(false);
    }
  }

  /** «Заменить партию» с причиной — тот же скан с подтверждением. */
  async function confirmLotMismatch(reason: string) {
    const mismatch = marks.takeLotMismatch();
    if (mismatch) await scan(mismatch.code, { confirmLotMismatch: true, reason });
  }

  async function setQty(itemId: string, qty: number) {
    if (!taskId) return;
    // Ручная отметка строки — прежняя отложенная отметка больше не нужна.
    marks.clearPending();
    await run(async () => {
      try {
        return await pickingApi.pickItem(taskId, itemId, {
          qtyPicked: qty,
          confirmedBinCode: marks.confirmedBin ?? undefined,
        });
      } catch (err) {
        // Нужен скан ячейки — запоминаем отметку, повторим после `LOC:`.
        marks.deferIfBinRequired(err, { type: "pick", itemId, qty });
        throw err;
      }
    });
  }

  const markShortage = useCallback(
    async (
      itemId: string,
      input: { shortageQty: number; reason: PickingShortageReason; note?: string },
    ) => {
      if (!taskId) return false;
      return run(() => pickingApi.itemShortage(taskId, itemId, input), {
        silent: true,
      });
    },
    [taskId, run],
  );

  const clearShortage = useCallback(
    async (itemId: string) => {
      if (!taskId) return false;
      // Toast обязателен: строка меняется неприметно (уходит красный фон),
      // а действие возвращает позицию в работу — это надо подтвердить словами.
      const ok = await run(() => pickingApi.clearItemShortage(taskId, itemId));
      if (ok) notify.success("Отметка недостачи снята — позицию можно собирать");
      return ok;
    },
    [taskId, run, notify],
  );

  const complete = useCallback(async () => {
    if (!taskId) return false;
    const ok = await run(() => pickingApi.complete(taskId), { silent: true });
    if (ok) notify.success("Сборка проведена, заказ переведён в «Собран»");
    return ok;
  }, [taskId, run, notify]);

  const reportShortage = useCallback(
    async (note: string) => {
      if (!taskId) return false;
      const ok = await run(() => pickingApi.shortage(taskId, note), { silent: true });
      if (ok) notify.warning("Недостача оформлена — задание ушло менеджеру");
      return ok;
    },
    [taskId, run, notify],
  );

  return {
    task,
    loading,
    loadError,
    busy,
    actionError,
    clearActionError: () => setActionError(null),
    scannedItemId,
    reload,
    claim,
    release,
    cancel,
    rebuild,
    scan,
    binRequest: marks.binRequest,
    lotMismatch: marks.lotMismatch,
    confirmLotMismatch,
    cancelLotMismatch: () => void marks.takeLotMismatch(),
    setQty,
    markShortage,
    clearShortage,
    complete,
    reportShortage,
  };
}

/** Задание перехватили/закрыли параллельно — состояние экрана протухло. */
function isStaleConflict(err: unknown): boolean {
  return err instanceof ApiError && err.code === "picking_task_claimed";
}
